import { Box, Dialog, Paper, type Theme, useMediaQuery } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Button from '/@/components/button';
import Icon from '/@/components/icon';
import MapComponent from '/@/components/map';
import NewOverlayListMobile from '/@/components/map/new-overlay-list-mobile';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import type { Field } from '/@/types/field';
import { type BoundsLiteral, toBoundsLiteral } from './bounds';

/** Geographic bounds of one humidity overlay version. */
type HumidityMapBounds = BoundsLiteral;

const HumidityMapModal = ({
    open,
    toggleHumidityMapModal,
    field,
    mapVersion,
    handleOverlayChange,
}: {
    open: boolean;
    toggleHumidityMapModal: (open: boolean) => void;
    /** Drawn on the map when there is one. The product-first wizard opens this before a
     * field is chosen, and the overlay bounds alone are enough to frame it. */
    field?: Field;
    /**
     * The wrapper is always supplied, but `version` only resolves once the overlay query
     * returns — the difference that matters, since the bounds are read from it.
     */
    // biome-ignore lint/suspicious/noExplicitAny: `Map`/`NewOverlayListMobile` are untyped JS.
    mapVersion: { version?: HumidityMapBounds; overlay?: any };
    /** Receives the newly picked overlay; `version` indexes the overlay's version list. */
    handleOverlayChange: (overlay?: { version?: number }) => void;
}) => {
    const mapRef = useRef<{ fitBounds?: (bounds: HumidityMapBounds, padding: number) => void } | null>(null);
    const sessionCountry = useSelector(getSessionCountry);
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

    const tabs = useCallback(() => {
        return [{ icon: 'humidity', label: _('Humedad en el suelo'), type: 'humidity' }];
    }, []);

    useEffect(() => {
        // `mapVersion` is a wrapper that always exists; the bounds inside it don't until the
        // overlay query resolves, so this has to key on the version rather than the wrapper.
        // The version carries the whole serialized map, so it has to be narrowed to the four
        // coordinates — Google rejects a bounds literal with any extra property.
        const bounds = toBoundsLiteral(mapVersion?.version);
        if (!bounds || !open) return;

        const timer = setTimeout(() => {
            mapRef.current?.fitBounds?.(bounds, 16);
        }, 100);

        return () => clearTimeout(timer);
    }, [mapVersion, open]);

    return (
        <Dialog open={open} onClose={toggleHumidityMapModal} maxWidth='lg' fullWidth>
            <Paper sx={{ height: isMobile ? '95vh' : '80vh' }}>
                <MapComponent
                    ref={mapRef}
                    overlay={mapVersion}
                    onOverlayChange={handleOverlayChange}
                    noGeocoder
                    simplifyPolygons
                    fields={field ? [field] : []}
                    zoomPosition='BOTTOM_RIGHT'
                    country={sessionCountry?.code || 'AR'}
                />
                <Box
                    sx={{
                        top: '1.6rem',
                        right: '1.6rem',
                        position: 'absolute',
                    }}
                    bgcolor='white'
                >
                    <Button variant='contained' onClick={toggleHumidityMapModal} color='inherit'>
                        <Icon type='close' size='sm' />
                    </Button>
                </Box>
                {isMobile && (
                    <NewOverlayListMobile
                        squashSingle
                        overlays={[mapVersion.overlay]}
                        selected={mapVersion}
                        overlay={mapVersion}
                        onOverlayClick={handleOverlayChange}
                        handleOverlayChange={handleOverlayChange}
                        tabs={tabs}
                    />
                )}
            </Paper>
        </Dialog>
    );
};

export default HumidityMapModal;
